// utils
import { axiosServerData, initRequestParam, NetsuiteInstanceProps } from 'src/utils/axios'
// ----------------------------------------------------------------------

const param = { firstEndPoint: 'init' } as NetsuiteInstanceProps

/**
 * Initialise l'application avec toutes les données essentielles en UNE SEULE requête.
 *
 * Remplace les multiples appels :
 * - getProfile
 * - getRoles
 * - getAccesses
 * - getSupplies
 * - getAllUsers
 * - listProducts (limit=10000)
 *
 * Les données lourdes (produits, utilisateurs) sont envoyées progressivement via WebSocket.
 *
 * @param idUser - ID Cognito de l'utilisateur
 * @returns Données d'initialisation complètes
 */
export const initializeApp = (idUser: string) => {
  initRequestParam(param, { idUser, lastEndPoint: 'root', method: 'get' } as NetsuiteInstanceProps)
  return axiosServerData(param)
}

/**
 * Demande un re-stream des données via WebSocket (utile si perte de connexion).
 *
 * @param idUser - ID Cognito de l'utilisateur
 * @param entity - Type d'entité à streamer ('products' ou 'users')
 * @param config - ID de la configuration
 * @returns Confirmation du streaming
 */
export const requestStreamData = (idUser: string, entity: 'products' | 'users', config: string) => {
  initRequestParam(param, {
    idUser,
    lastEndPoint: 'stream',
    method: 'post',
    data: { entity }
  } as NetsuiteInstanceProps)
  param.headers = { config }
  return axiosServerData(param)
}

/**
 * Récupère les produits de manière paginée (pour les grands catalogs >2000 produits)
 *
 * @param idUser - ID Cognito de l'utilisateur
 * @param config - ID de la configuration
 * @param page - Numéro de page (défaut : 1)
 * @param limit - Items par page (défaut : 500, max : 1000)
 * @param search - Recherche textuelle optionnelle
 * @param category - Filtre par catégorie optionnel
 * @returns Produits paginés avec métadonnées
 */
export const getPaginatedProducts = (
  idUser: string,
  config: string,
  page: number = 1,
  limit: number = 500,
  search?: string,
  category?: string
) => {
  const queryParams = new URLSearchParams()
  queryParams.append('page', page.toString())
  queryParams.append('limit', limit.toString())
  if (search) queryParams.append('search', search)
  if (category) queryParams.append('category', category)

  initRequestParam(param, {
    idUser,
    lastEndPoint: `products?${queryParams.toString()}`,
    method: 'get',
  } as unknown as NetsuiteInstanceProps);
  param.headers = { config }
  return axiosServerData(param)
}

/**
 * Charge tous les produits paginés en parcourant toutes les pages.
 * Utilisé quand le streaming WebSocket est désactivé (trop de produits).
 *
 * @param idUser - ID Cognito de l'utilisateur
 * @param config - ID de la configuration
 * @param onProgress - Callback appelé à chaque page chargé (page, totalPages, items)
 * @returns Tous les produits agrégés
 */
export const loadAllPaginatedProducts = async (
  idUser: string,
  config: string,
  onProgress?: (page: number, totalPages: number, itemsLoaded: number) => void
): Promise<any[]> => {
  const allProducts: any[] = []
  let page = 1
  let totalPages = 1

  do {
    const result = await getPaginatedProducts(idUser, config, page, 500)
    const { data, pagination } = result?.data || {}

    if (data && Array.isArray(data)) {
      allProducts.push(...data)
    }

    totalPages = pagination?.totalPages || 1

    if (onProgress) {
      onProgress(page, totalPages, allProducts.length)
    }

    page++
  } while (page <= totalPages)

  return allProducts
}

/**
 * Vérifie si les données en cache sont complètes et demande un re-stream si nécessaire.
 * Utile après une reconnexion WebSocket pour s'assurer que toutes les données sont présentes.
 *
 * @param idUser - ID Cognito de l'utilisateur
 * @param config - ID de la configuration
 * @returns Statut des données manquantes
 */
export const checkMissingData = async (idUser: string, config: string) => {
  try {
    const { getInitCache } = await import('src/utils/indexeddb-cache');
    const cache = await getInitCache();

    const missing: ('products' | 'users')[] = [];

    if (!cache?.products || cache.products.length === 0) {
      missing.push('products');
    }
    if (!cache?.users || cache.users.length === 0) {
      missing.push('users');
    }

    // Demander le re-stream des données manquantes
    for (const entity of missing) {
      console.info(`[INIT] Requesting re-stream for missing ${entity}`);
      await requestStreamData(idUser, entity, config);
    }

    return {
      hasAllData: missing.length === 0,
      missing,
      cachedProducts: cache?.products?.length || 0,
      cachedUsers: cache?.users?.length || 0,
    };
  } catch (error) {
    console.error('[INIT] Error checking missing data:', error);
    return {
      hasAllData: false,
      missing: ['products', 'users'] as ('products' | 'users')[],
      cachedProducts: 0,
      cachedUsers: 0,
    };
  }
}
